import { ART_PALETTE } from '../art-palette.js';
import { BLOB_MASKS, MASK } from './masks.js';

/**
 * Blob-47 surface factory — flat hybrid house style (art-direction-rules §0).
 *
 * Two rules drive every decision here:
 *
 * 1. **Interiors are flat.** A blob-47 interior is a single tile (mask 255), so
 *    *any* mark inside it repeats on an exact 16px pitch and reads as wallpaper
 *    across an open field. Grass, dark grass and dirt therefore carry no centre
 *    texture at all; the `flora.*` overlay tiles exist to break up open ground.
 *    Rock is no exception: a fracture *network* is impossible too, because a
 *    line can only continue across a join if its entry row equals its exit row,
 *    so every continuous fracture is 16px-periodic and a network of them closes
 *    into one-tile cells that render as course-lined brickwork.
 * 2. **Detail lives on the boundary**, which is where the 47 masks actually
 *    differ and where nothing can repeat. The boundary is a value step, never a
 *    contour: one pixel on the lit north/west side, two on the shadow-facing
 *    south/east side (§2 — strengthen the shadow-side silhouette, do not
 *    thicken all edges equally). Rock adds a lit cap band there, and that is
 *    the whole of its relief.
 *
 * Silhouette profiles move in runs of three pixels or more, and were picked by
 * scoring every run-legal profile against the seam checker. A per-pixel 1/2
 * sawtooth makes the boundary step land on alternating pixels, and the edge
 * then reads as a dashed stitch rather than as a shore.
 */

function surfaceVariants({ fill, edge, litCap, profile }) {
  return Object.fromEntries(BLOB_MASKS.map((mask) => {
    const inside = (x, y) => {
      const north = Boolean(mask & MASK.N); const east = Boolean(mask & MASK.E);
      const south = Boolean(mask & MASK.S); const west = Boolean(mask & MASK.W);
      const outsideWest = !west && x < profile[y];
      const outsideEast = !east && x >= 16 - profile[(y + 5) % 16];
      const outsideNorth = !north && y < profile[(x + 3) % 16];
      const outsideSouth = !south && y >= 16 - profile[(x + 9) % 16];
      if (!(outsideWest || outsideEast || outsideNorth || outsideSouth)) return true;
      if (outsideWest && outsideNorth) return Boolean(mask & MASK.NW);
      if (outsideEast && outsideNorth) return Boolean(mask & MASK.NE);
      if (outsideEast && outsideSouth) return Boolean(mask & MASK.SE);
      if (outsideWest && outsideSouth) return Boolean(mask & MASK.SW);
      return false;
    };
    const occupiedAt = (x, y) => {
      if (x >= 0 && x < 16 && y >= 0 && y < 16) return inside(x, y);
      if (x < 0 && y < 0) return Boolean(mask & MASK.NW);
      if (x >= 16 && y < 0) return Boolean(mask & MASK.NE);
      if (x >= 16 && y >= 16) return Boolean(mask & MASK.SE);
      if (x < 0 && y >= 16) return Boolean(mask & MASK.SW);
      if (y < 0) return Boolean(mask & MASK.N);
      if (x >= 16) return Boolean(mask & MASK.E);
      if (y >= 16) return Boolean(mask & MASK.S);
      return Boolean(mask & MASK.W);
    };

    const pixels = Array.from({ length: 16 }, (_, y) =>
      Array.from({ length: 16 }, (_, x) => (inside(x, y) ? fill : 0)));

    // Boundary value step. Both passes read occupancy, never the painted
    // pixels, so the step is a pure function of the mask — which is what keeps
    // it identical on both sides of a legal join.
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        if (!pixels[y][x]) continue;
        const contour = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]]
          .some(([dx, dy]) => !occupiedAt(x + dx, y + dy));
        // Second step on the shadow-facing sides only (light is upper-left).
        const shadowSide = !occupiedAt(x, y + 2) || !occupiedAt(x + 2, y);
        if (contour || shadowSide) {
          pixels[y][x] = edge;
          continue;
        }
        // Lit cap: a band just inside the contour on the north/west rim, so an
        // outcrop reads as a plateau with a top face catching the upper-left
        // key. Rock only, and the one place rock gets to carry relief — any
        // mark in the interior is mask 255 and repeats as a 16px lattice.
        if (!litCap) continue;
        const lit = Array.from({ length: litCap.depth }, (_, index) => index + 2)
          .some((d) => !occupiedAt(x, y - d) || !occupiedAt(x - d, y));
        if (lit) pixels[y][x] = litCap.color;
      }
    }

    return [mask, pixels];
  }));
}

function surfaceTile({ key, name, connectionGroup, fill, edge, litCap, profile }) {
  const runs = []; let run = 1;
  for (let i = 1; i < 16; i++) {
    if (profile[i] === profile[i - 1]) run += 1;
    else { runs.push(run); run = 1; }
  }
  // The profile is a ring: when tail matches head, the final run continues the
  // first one and must be merged, or a short wrap-around run slips through.
  if (runs.length && profile[0] === profile[15]) runs[0] += run;
  else runs.push(run);
  if (runs.length > 1 && runs.some((length) => length < 3)) {
    throw new Error(`${key}: profile has a run shorter than 3 and will read as a dashed edge`);
  }
  return {
    key, name, channel: 'surface', mode: 'blob8', connectionGroup,
    palette: ART_PALETTE, collision: { ground: false },
    components: { ground: surfaceVariants({ fill, edge, litCap, profile }) },
  };
}

// ---------------------------------------------------------------------------
// Dirt — flat Earth (8) field. Earth dark (7) boundary line where it meets
// other terrain, doubled on the shadow side. No grit: a speck in the centre of
// a blob-47 interior is a repeating glyph, and dirt patches are small enough
// that the boundary carries the whole read.
// ---------------------------------------------------------------------------
const DIRT_SURFACE = surfaceTile({
  key: 'surface.dirt', name: 'Dirt', connectionGroup: 'dirt',
  fill: 8, edge: 7,
  profile: [1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2],
});

// ---------------------------------------------------------------------------
// Grass — flat Grass (14) field, Dark grass (13) boundary step. Open meadow is
// deliberately untextured; relief comes from the edges, from object contact
// shadows, and from the flora overlay tiles.
// ---------------------------------------------------------------------------
const GRASS_SURFACE = surfaceTile({
  key: 'surface.grass', name: 'Grass', connectionGroup: 'grass',
  fill: 14, edge: 13,
  profile: [1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 1, 2, 2, 2, 1],
});
GRASS_SURFACE.parentSurface = 'surface.dirt';

// ---------------------------------------------------------------------------
// Dark grass — the same grammar one step darker: Dark grass (13) field with a
// Forest (12) boundary step.
// ---------------------------------------------------------------------------
const DARK_GRASS_SURFACE = surfaceTile({
  key: 'surface.dark-grass', name: 'Dark grass', connectionGroup: 'dark-grass',
  fill: 13, edge: 12,
  profile: [1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 1, 2, 2, 2, 2],
});
// Dark grass is the upper grass stratum. Its transparent transition pixels
// reveal ordinary grass first, then dirt beneath that, so grass-to-grass edges
// read as deliberate overlap instead of two unrelated dirt-bordered islands.
DARK_GRASS_SURFACE.parentSurface = 'surface.grass';

// ---------------------------------------------------------------------------
// Rock — cool Stone ramp. A flat Stone (26) top face, a Stone shadow
// (24) contact step doubled on the shadow side, and a two-pixel Stone light
// (27) cap along the lit rim, which is what makes an outcrop read as a stone
// plateau with a top face rather than a grey slab. All of its relief is
// boundary relief, because a mask-255 interior can only repeat.
// ---------------------------------------------------------------------------
const ROCK_SURFACE = surfaceTile({
  key: 'surface.rock', name: 'Rocky terrain', connectionGroup: 'rock',
  fill: 26, edge: 24, litCap: { color: 27, depth: 2 },
  profile: [1, 1, 1, 1, 1, 1, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2],
});
ROCK_SURFACE.parentSurface = 'surface.dirt';

export { GRASS_SURFACE, DARK_GRASS_SURFACE, DIRT_SURFACE, ROCK_SURFACE };
